import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { OpModal } from 'new-components/DLS/OpModal/OpModal';
import { OnSubmitArgs, OpForm } from 'new-components/DLS/OpForm/OpForm';
import { useSelectorJs } from 'utils/customHooks';
import { heliumSend } from 'utils/helpers';
import { notificationService } from 'containers/AlertWrapper/notificationService';
import { getOpParams } from 'utils/getOpParams';

import {
  DATE_TIME_AM_PM_FORMAT,
  DATE_TIME_FORMAT,
} from '../../../../../constants/dates';
import STATUS from '../../../../../constants/status';
import { AppDispatch, ImmutableRootState } from '../../../../../store/store';
import { toLocalByTimezone } from '../../../../../utils/dates';
import {
  fetchVisits,
  VisitsState,
} from '../../../../../store/slices/visitsSlice';
import {
  searchVisitors,
  searchVisitorsToday,
  VisitorSearchState,
} from '../../../../../store/slices/visitorSearchSlice';
import { VisitorInvitationState } from '../../../../../store/slices/visitorInvitationSlice';

dayjs.extend(utc);
dayjs.extend(timezone);

interface ProfileEditModalProps {
  open: boolean;
  onClose: () => void;
}

interface ProfileEditFormValues {
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
  email?: string | null;
  mobilePhone?: string | null;
  signIn?: dayjs.ConfigType | null;
  signOut?: dayjs.ConfigType | null;
}

const ProfileEditModal = ({ open, onClose }: ProfileEditModalProps) => {
  const { t } = useTranslation();
  const [form] = OpForm.useForm();

  const dispatch: AppDispatch = useDispatch();

  const { orgId } = getOpParams();

  const selectedVisit = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['visits', 'selectedVisit']),
  ) as VisitsState['selectedVisit'];
  const selectedVisitor = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['visits', 'selectedVisitor']),
  ) as VisitsState['selectedVisitor'];
  const invitationConfig = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['visitorInvitation', 'invitationConfig']),
  ) as VisitorInvitationState['invitationConfig'];
  const globalLocationId = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['locations', 'globalLocation', 'id']),
  ) as number | undefined;
  const lastSearchParams = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['visitorSearch', 'lastSearchParams']),
  ) as VisitorSearchState['lastSearchParams'];

  const tz = selectedVisit?.location?.timezone;

  const getDisabledTime = (current: dayjs.ConfigType | null) => {
    if (!current) return {};

    const nowInTz = dayjs().tz(tz || 'UTC');

    // Convert current date to location timezone for comparison
    const currentDateStr = dayjs(current).format('YYYY-MM-DD');
    const currentInTz = dayjs.tz(currentDateStr, tz || 'UTC');
    const isToday = currentInTz.isSame(nowInTz, 'day');

    // For past dates, don't disable any time (all times are valid)
    if (!isToday) return {};

    const currentHour = nowInTz.hour();
    const currentMinute = nowInTz.minute();

    // For today, disable future hours and minutes (only past is valid)
    return {
      disabledHours: () =>
        Array.from({ length: 24 }, (_, i) => i).filter((h) => h > currentHour),
      disabledMinutes: (selectedHour: number) => {
        if (selectedHour > currentHour) {
          return Array.from({ length: 60 }, (_, i) => i);
        }
        if (selectedHour === currentHour) {
          return Array.from({ length: 60 }, (_, i) => i).filter(
            (m) => m > currentMinute,
          );
        }
        return [];
      },
    };
  };

  const initialValues = {
    ...selectedVisitor,
    firstName: selectedVisitor?.firstName || '',
    middleName: selectedVisitor?.middleName || '',
    lastName: selectedVisitor?.lastName || '',
    email: selectedVisitor?.email || '',
    mobilePhone: selectedVisitor?.mobilePhone || '',
    signIn: selectedVisitor?.signIn
      ? toLocalByTimezone(selectedVisitor.signIn, tz)?.format(
          'YYYY-MM-DDTHH:mm:ss',
        )
      : null,
    signOut: selectedVisitor?.signOut
      ? toLocalByTimezone(selectedVisitor.signOut, tz)?.format(
          'YYYY-MM-DDTHH:mm:ss',
        )
      : null,
  };

  const editVisitor = async (updatedVisitor: ProfileEditFormValues) => {
    try {
      const payload = {
        ...(updatedVisitor.firstName != null && {
          firstName: updatedVisitor.firstName.trim(),
        }),
        ...(updatedVisitor.middleName != null && {
          middleName: updatedVisitor.middleName.trim(),
        }),
        ...(updatedVisitor.lastName != null && {
          lastName: updatedVisitor.lastName.trim(),
        }),
        ...(updatedVisitor.email != null && { email: updatedVisitor.email }),
        ...(updatedVisitor.mobilePhone != null && {
          mobilePhone: updatedVisitor.mobilePhone,
        }),
        ...(updatedVisitor.signIn != null && {
          signIn: dayjs
            .tz(
              dayjs(updatedVisitor.signIn).format('YYYY-MM-DD HH:mm:ss'),
              tz || 'UTC',
            )
            .utc()
            .format(DATE_TIME_FORMAT),
        }),
        ...(updatedVisitor.signOut != null && {
          signOut: dayjs
            .tz(
              dayjs(updatedVisitor.signOut).format('YYYY-MM-DD HH:mm:ss'),
              tz || 'UTC',
            )
            .utc()
            .format(DATE_TIME_FORMAT),
        }),
      };

      if (
        payload.signOut &&
        payload.signIn &&
        dayjs(payload.signOut).isBefore(dayjs(payload.signIn))
      ) {
        notificationService.show(
          'error',
          t('visitors.profile.notification.invalidDates'),
        );
        onClose();
        return;
      }

      try {
        await heliumSend(
          'updateVisitors',
          [orgId, selectedVisit?.id, selectedVisitor?.id],
          payload,
        );
        dispatch(fetchVisits({ orgId }));
        dispatch(searchVisitorsToday({ orgId }));
        if (Object.keys(lastSearchParams).length !== 0) {
          dispatch(
            searchVisitors({
              ...lastSearchParams,
              orgId,
              locationId: lastSearchParams.locationId || globalLocationId!,
            }),
          );
        }

        onClose();
        notificationService.show(
          'success',
          t('visitors.profile.notification.updateSuccess'),
        );
      } catch {
        // Failed to update visitor
      }
    } catch {
      notificationService.show(
        'error',
        t('visitors.profile.notification.updateError'),
      );
    }
  };

  return (
    <OpModal
      open={open}
      title={t('visitors.profile.edit.title')}
      okText={t('visitors.profile.edit.save')}
      cancelText={t('visitors.profile.edit.cancel')}
      width={720}
      onCancel={() => {
        form.resetFields();
        onClose();
      }}
      onOk={() => {
        form
          .validateFields()
          .then((_values) => {
            form.submit();
            onClose();
          })
          .catch((error) => {
            notificationService.show(
              'error',
              error.message || t('notification.validationError'),
            );
          });
      }}
      centered
    >
      <OpForm
        form={form}
        initialValues={initialValues}
        onSubmit={({ values }: OnSubmitArgs<ProfileEditFormValues>) => {
          editVisitor(values);
          form.resetFields();
        }}
        defaultButtons={false}
        hasError={false}
      >
        {selectedVisitor?.status === STATUS.PENDING.id && (
          <>
            <OpForm.Input
              label={t('visitors.profile.edit.form.firstName')}
              name="firstName"
              rules={[
                {
                  required: true,
                  message: t('visitors.profile.edit.form.firstNameRequired'),
                },
              ]}
            />
            <OpForm.Input
              label={t('visitors.profile.edit.form.lastName')}
              name="lastName"
              rules={[
                {
                  required: true,
                  message: t('visitors.profile.edit.form.lastNameRequired'),
                },
              ]}
            />
            <OpForm.Input
              label={t('visitors.profile.edit.form.middleName')}
              name="middleName"
            />
            <OpForm.Input
              label={t('visitors.profile.edit.form.email')}
              name="email"
              rules={[
                {
                  required:
                    selectedVisit?.visitStatus.id === STATUS.PENDING.id &&
                    invitationConfig?.email.required === 1,
                  message: t('visitors.profile.edit.form.emailRequired'),
                },
              ]}
            />
            <OpForm.PhoneInput
              label={t('visitors.profile.edit.form.mobilePhone')}
              name="mobilePhone"
              rules={[
                {
                  required:
                    selectedVisit?.visitStatus.id === STATUS.PENDING.id &&
                    invitationConfig?.visitPhone.required === 1,
                  message: t('visitors.profile.edit.form.mobilePhoneRequired'),
                },
              ]}
            />
          </>
        )}
        {selectedVisitor?.status === STATUS.SIGNED_IN.id && ( // Sign In time field is required when status is 'Signed In'
          <OpForm.DatePicker
            format={DATE_TIME_AM_PM_FORMAT}
            name="signIn"
            label={t('visitors.profile.edit.form.signIn')}
            minuteStep={5}
            showTime
            disabledDate={(current) => {
              if (!current) return false;
              const currentDateStr = dayjs(current).format('YYYY-MM-DD');
              const currentInTz = dayjs.tz(currentDateStr, tz || 'UTC');
              const nowInTz = dayjs().tz(tz || 'UTC');
              return currentInTz.isAfter(nowInTz, 'day');
            }}
            disabledTime={getDisabledTime}
            rules={[
              {
                required: true,
                message: t('visitors.header.editModal.form.dateRequired'),
              },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  const nowInTz = dayjs().tz(tz || 'UTC');
                  // Convert value to location timezone and compare with current time
                  const valueStr = dayjs(value).format('YYYY-MM-DD HH:mm:ss');
                  const valueInTz = dayjs.tz(valueStr, tz || 'UTC');
                  return valueInTz.isAfter(nowInTz)
                    ? Promise.reject(
                        new Error(
                          t(
                            'visitors.header.editModal.notification.invalidSignInTimeError',
                          ),
                        ),
                      )
                    : Promise.resolve();
                },
              },
            ]}
          />
        )}

        {selectedVisitor?.status === STATUS.SIGNED_OUT.id && (
          <>
            <OpForm.DatePicker
              format={DATE_TIME_AM_PM_FORMAT}
              name="signIn"
              label={t('visitors.profile.edit.form.signIn')}
              minuteStep={5}
              showTime
              disabledDate={(current) => {
                if (!current) return false;
                const currentDateStr = dayjs(current).format('YYYY-MM-DD');
                const currentInTz = dayjs.tz(currentDateStr, tz || 'UTC');
                const nowInTz = dayjs().tz(tz || 'UTC');
                return currentInTz.isAfter(nowInTz, 'day');
              }}
              disabledTime={getDisabledTime}
              rules={[
                {
                  required: true,
                  message: t('visitors.profile.edit.form.dateRequired'),
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value) return Promise.resolve();

                    const signOut = getFieldValue('signOut');
                    const nowInTz = dayjs().tz(tz || 'UTC');
                    const valueDayjs = dayjs(value);

                    // Convert value to location timezone and compare with current time
                    const valueStr = valueDayjs.format('YYYY-MM-DD HH:mm:ss');
                    const valueInTz = dayjs.tz(valueStr, tz || 'UTC');

                    if (valueInTz.isAfter(nowInTz)) {
                      return Promise.reject(
                        new Error(
                          t(
                            'visitors.header.editModal.notification.invalidSignInTimeError',
                          ),
                        ),
                      );
                    }

                    if (signOut) {
                      const signOutDayjs = dayjs(signOut);
                      if (valueDayjs.isSameOrAfter(signOutDayjs)) {
                        return Promise.reject(
                          new Error(
                            t(
                              'visitors.header.editModal.notification.startDateAfterEndDateError',
                            ),
                          ),
                        );
                      }
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
              onChange={() => form.validateFields(['signOut'])}
            />
            <OpForm.DatePicker
              format={DATE_TIME_AM_PM_FORMAT}
              name="signOut"
              label={t('visitors.profile.edit.form.signOut')}
              minuteStep={5}
              showTime
              disabledDate={(current) => {
                if (!current) return false;
                const currentDateStr = dayjs(current).format('YYYY-MM-DD');
                const currentInTz = dayjs.tz(currentDateStr, tz || 'UTC');
                const nowInTz = dayjs().tz(tz || 'UTC');
                return currentInTz.isAfter(nowInTz, 'day');
              }}
              disabledTime={getDisabledTime}
              rules={[
                {
                  required: true,
                  message: t('visitors.profile.edit.form.dateRequired'),
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value) return Promise.resolve();

                    const signIn = getFieldValue('signIn');
                    const nowInTz = dayjs().tz(tz || 'UTC');
                    const valueDayjs = dayjs(value);

                    // Convert value to location timezone and compare with current time
                    const valueStr = valueDayjs.format('YYYY-MM-DD HH:mm:ss');
                    const valueInTz = dayjs.tz(valueStr, tz || 'UTC');

                    if (valueInTz.isAfter(nowInTz)) {
                      return Promise.reject(
                        new Error(
                          t(
                            'visitors.header.editModal.notification.invalidSignOutTimeError',
                          ),
                        ),
                      );
                    }

                    if (signIn) {
                      const signInDayjs = dayjs(signIn);
                      if (valueDayjs.isSameOrBefore(signInDayjs)) {
                        return Promise.reject(
                          new Error(
                            t(
                              'visitors.header.editModal.notification.invalidDateRangeError',
                            ),
                          ),
                        );
                      }
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
              onChange={() => form.validateFields(['signIn'])}
            />
          </>
        )}
      </OpForm>
    </OpModal>
  );
};

export default ProfileEditModal;
